/**
 * Event Priority Manager
 * Prevents duplicate event firing based on priority rules
 */

import { EVENT_PRIORITIES } from './constants.js';
import { ANALYTICS_CONFIG } from './constants.js';

// Track recent events to prevent duplicates
const event_history = new Map();
const DUPLICATE_THRESHOLD_MS = 500; // 500ms window for duplicate detection

/**
 * Check if event should be blocked based on priority rules
 * Priority order: file_download > menu_click > cta_click > hyperlink_click
 * @param {string} event_name - Event name to check
 * @returns {boolean} - True if event should be blocked
 */
export function should_block_event(event_name) {
  // Get priority of current event
  const current_priority = EVENT_PRIORITIES[event_name];

  // If event not in priority list, allow it
  if (current_priority === undefined) {
    return false;
  }

  // Check recent events in history
  const now = Date.now();

  for (const [history_event, timestamp] of event_history.entries()) {
    // Only check events within threshold window
    if (now - timestamp > DUPLICATE_THRESHOLD_MS) {
      continue;
    }

    const history_priority = EVENT_PRIORITIES[history_event];

    // If a higher priority event was just fired, block lower priority events
    if (history_priority !== undefined && history_priority < current_priority) {
      if (ANALYTICS_CONFIG.debug) {
        console.log(`[Analytics] Blocked ${event_name} due to recent ${history_event}`);
      }
      return true;
    }
  }

  return false;
}

/**
 * Record event in history
 * @param {string} event_name - Event name to record
 */
export function record_event(event_name) {
  event_history.set(event_name, Date.now());

  // Cleanup old entries
  const now = Date.now();
  for (const [key, timestamp] of event_history.entries()) {
    if (now - timestamp > DUPLICATE_THRESHOLD_MS * 2) {
      event_history.delete(key);
    }
  }
}

/**
 * Clear event history (useful for testing)
 */
export function clear_event_history() {
  event_history.clear();
}

/**
 * Get event history for debugging
 * @returns {Array} - Array of recorded events with timestamps
 */
export function get_event_history() {
  return Array.from(event_history.entries()).map(([event, timestamp]) => ({
    event,
    timestamp,
    age_ms: Date.now() - timestamp
  }));
}
